import {
  Box,
  Button,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { ChangeEvent, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  useGetCompanyByIdQuery,
  useUpdateCompanyByIdMutation,
  useUpdateUserCompanyAssociationMutation,
} from 'store/company/company.api'
import { CompanyAssociation } from 'store/company/company.types'
import { useUpdateUserFeatureMutation } from 'store/auth/auth.api'
import { RootState } from 'store'
import { isCompanyFeatureEnabled } from 'utils/features'
import { setUserCompanyFeature } from 'store/auth/auth.slice'
import {
  useGeneratePortalAccessMutation,
  useGetPortalAccessQuery,
  useRevokePortalAccessMutation,
} from 'store/customer-portal/customer-portal.api'
import {
  DetailField,
  detailTextFieldSx,
  greySectionSx,
  primaryActionButtonSx,
  toggleRowSx,
} from '../customer-detail-fields'

interface CustomerCompanyNameTabProps {
  association: CompanyAssociation
  onDirtyChange: (dirty: boolean) => void
  saveToken: number
  onSaved: () => void
}

const CustomerCompanyNameTab = ({
  association,
  onDirtyChange,
  saveToken,
  onSaved,
}: CustomerCompanyNameTabProps) => {
  const dispatch = useDispatch()
  const companyId = association.companyId
  const userId = association.userId

  const { data: company } = useGetCompanyByIdQuery({ id: companyId })
  const [updateCompany, { isLoading: isUpdatingCompany }] =
    useUpdateCompanyByIdMutation()
  const [updateAssociation, { isLoading: isUpdatingAssociation }] =
    useUpdateUserCompanyAssociationMutation()
  const [updateFeature, { isLoading: isUpdatingFeature }] =
    useUpdateUserFeatureMutation()

  const featuresByCompany = useSelector(
    (state: RootState) => state.auth.featuresByCompany
  )

  const [companyName, setCompanyName] = useState('')
  const [legalCompanyName, setLegalCompanyName] = useState('')
  const [factoringEnabled, setFactoringEnabled] = useState(
    association.factoringEnabled
  )

  const waybillEnabled = useMemo(
    () => isCompanyFeatureEnabled(featuresByCompany, companyId, 'WAYBILL'),
    [featuresByCompany, companyId]
  )
  const [waybill, setWaybill] = useState(waybillEnabled)

  const { data: portalAccess } = useGetPortalAccessQuery({ customerId: companyId })
  const [generatePortal, { isLoading: isGeneratingPortal }] =
    useGeneratePortalAccessMutation()
  const [revokePortal, { isLoading: isRevokingPortal }] =
    useRevokePortalAccessMutation()
  const [freshLinkUrl, setFreshLinkUrl] = useState<string | null>(null)
  const [linkCopied, setLinkCopied] = useState(false)

  useEffect(() => {
    if (company?.companyName != null) {
      setCompanyName(company.companyName)
      setLegalCompanyName(company.companyName)
    }
  }, [company?.companyName, companyId])

  useEffect(() => {
    setFactoringEnabled(association.factoringEnabled)
  }, [association.factoringEnabled, association.id])

  useEffect(() => {
    setWaybill(waybillEnabled)
  }, [waybillEnabled])

  useEffect(() => {
    setFreshLinkUrl(null)
    setLinkCopied(false)
  }, [companyId])

  const isDirty =
    company != null &&
    (companyName.trim() !== (company.companyName ?? '') ||
      legalCompanyName.trim() !== (company.companyName ?? ''))

  useEffect(() => {
    onDirtyChange(isDirty)
  }, [isDirty, onDirtyChange])

  useEffect(() => {
    if (!saveToken) return

    const save = async () => {
      const trimmedName = companyName.trim()
      if (!companyId || !trimmedName) return

      try {
        if (trimmedName !== company?.companyName) {
          await updateCompany({
            id: companyId,
            companyName: trimmedName,
          }).unwrap()
        }
        onSaved()
      } catch (error) {
        console.error('Failed to update customer', error)
      }
    }

    void save()
    // Intentionally keyed on saveToken only — captures form state at save time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveToken])

  const handleFactoringChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked
    setFactoringEnabled(newValue)
    try {
      await updateAssociation({
        id: association.id,
        userId: association.userId,
        companyId: association.companyId,
        association: association.association,
        factoringTerm: association.factoringTerm,
        currency: association.currency,
        factoringEnabled: newValue,
      }).unwrap()
    } catch (error) {
      console.error('Error while updating factoring', error)
      setFactoringEnabled(association.factoringEnabled)
    }
  }

  const handleWaybillChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const enabled = event.target.checked
    setWaybill(enabled)
    try {
      await updateFeature({
        userId,
        companyId,
        featureName: 'WAYBILL',
        enabled,
      }).unwrap()
      dispatch(
        setUserCompanyFeature({ companyId, featureName: 'WAYBILL', enabled })
      )
    } catch (error) {
      console.error('Error while updating waybill', error)
      setWaybill(waybillEnabled)
    }
  }

  const handleGenerateLink = async () => {
    try {
      const res = await generatePortal({ customerId: companyId }).unwrap()
      setFreshLinkUrl(res.linkUrl)
      try {
        await navigator.clipboard.writeText(res.linkUrl)
        setLinkCopied(true)
      } catch {
        setLinkCopied(false)
      }
    } catch (e) {
      console.error('Failed to generate portal link', e)
    }
  }

  const handleCopyLink = async () => {
    const url = freshLinkUrl
    if (!url) return
    try {
      await navigator.clipboard.writeText(url)
      setLinkCopied(true)
    } catch (e) {
      console.error('Failed to copy', e)
    }
  }

  const handleRevokeLink = async () => {
    try {
      await revokePortal({ customerId: companyId }).unwrap()
      setFreshLinkUrl(null)
      setLinkCopied(false)
    } catch (e) {
      console.error('Failed to revoke portal link', e)
    }
  }

  const portalInputValue = freshLinkUrl ?? ''
  const hasActivePortal = portalAccess?.exists || !!freshLinkUrl
  const isSaving =
    isUpdatingCompany || isUpdatingAssociation || isUpdatingFeature

  const bottomSectionSx = {
    width: '100%',
    maxWidth: 960,
    mx: 'auto',
  }

  const portalActionRowSx = {
    flexWrap: 'nowrap',
    alignItems: 'center',
  }

  const portalActionButtonSx = {
    ...primaryActionButtonSx,
    flexShrink: 0,
    whiteSpace: 'nowrap',
  }

  return (
    <Box
      sx={{
        px: 3,
        py: 2,
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
      }}
    >
      <Box sx={{ maxWidth: 640 }}>
        <DetailField label="Customer Name">
          <TextField
            fullWidth
            size="small"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            disabled={isSaving}
            sx={detailTextFieldSx}
          />
        </DetailField>

        <DetailField label="Legal Company Name">
          <TextField
            fullWidth
            size="small"
            value={legalCompanyName}
            onChange={(e) => setLegalCompanyName(e.target.value)}
            disabled={isSaving}
            sx={detailTextFieldSx}
          />
        </DetailField>
      </Box>

      <Box sx={{ flex: 1, minHeight: 32 }} />

      <Box sx={bottomSectionSx}>
        <Box sx={greySectionSx}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
            Customer Portal Access
          </Typography>

          {hasActivePortal ? (
            <>
              <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
                Share this link with the customer.
              </Typography>
              <Stack direction="row" spacing={1} sx={portalActionRowSx}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="link name"
                  value={portalInputValue}
                  inputProps={{ readOnly: true }}
                  onFocus={(e) => e.target.select()}
                  sx={{ ...detailTextFieldSx, minWidth: 0 }}
                />
                {freshLinkUrl ? (
                  <Button
                    variant="contained"
                    onClick={handleCopyLink}
                    sx={portalActionButtonSx}
                  >
                    {linkCopied ? 'Copied' : 'Copy'}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleGenerateLink}
                    disabled={isGeneratingPortal}
                    sx={portalActionButtonSx}
                  >
                    Generate Link
                  </Button>
                )}
                {(portalAccess?.exists || freshLinkUrl) && (
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleRevokeLink}
                    disabled={isRevokingPortal}
                    sx={portalActionButtonSx}
                  >
                    Revoke
                  </Button>
                )}
              </Stack>
            </>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
                No portal access yet. Generate a link to share with this customer.
              </Typography>
              <Stack direction="row" spacing={1} sx={portalActionRowSx}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="link name"
                  disabled
                  sx={{ ...detailTextFieldSx, minWidth: 0 }}
                />
                <Button
                  variant="contained"
                  onClick={handleGenerateLink}
                  disabled={isGeneratingPortal}
                  sx={portalActionButtonSx}
                >
                  Generate Link
                </Button>
              </Stack>
            </>
          )}
        </Box>

        <Box sx={toggleRowSx}>
          <Typography sx={{ fontWeight: 700 }}>Factored</Typography>
          <Switch
            checked={factoringEnabled}
            onChange={handleFactoringChange}
            disabled={isUpdatingAssociation}
          />
        </Box>

        <Box sx={{ ...toggleRowSx, mb: 0 }}>
          <Typography sx={{ fontWeight: 700 }}>Waybill</Typography>
          <Switch
            checked={waybill}
            onChange={handleWaybillChange}
            disabled={isUpdatingFeature}
          />
        </Box>
      </Box>
    </Box>
  )
}

export default CustomerCompanyNameTab
